/**
 * Accessible Newsletter Form Validation
 * Implements WCAG-compliant form validation for newsletter signup forms
 */

class NewsletterFormValidator {
  constructor(form) {
    this.form = form;
    this.emailInput = form.querySelector('input[type="email"]');
    this.submitButton = form.querySelector('button[type="submit"]');
    
    if (!this.emailInput) return;
    
    this.findOrCreateErrorBanner();
    this.init();
  }

  init() {
    // Add novalidate to disable HTML5 validation
    this.form.setAttribute('novalidate', '');
    
    // Set up aria-describedby relationship
    const errorId = `${this.emailInput.id}-error`;
    this.errorBanner.id = errorId;
    this.emailInput.setAttribute('aria-describedby', errorId);
    
    // Add event listeners - use capture phase to run before other handlers
    this.form.addEventListener('submit', this.handleSubmit.bind(this), true);
    this.emailInput.addEventListener('blur', this.validateEmail.bind(this));
    this.emailInput.addEventListener('input', this.clearError.bind(this));
  }

  findOrCreateErrorBanner() {
    // Look for existing error banner
    this.errorBanner = this.form.querySelector('.form__banner.banner--error');
    
    if (!this.errorBanner) {
      // Create error banner if it doesn't exist
      this.errorBanner = document.createElement('div');
      this.errorBanner.className = 'form__banner banner banner--error';
      this.errorBanner.style.display = 'none';
      
      // Create ribbon icon
      const ribbon = document.createElement('span');
      ribbon.className = 'banner__ribbon';
      ribbon.innerHTML = '<svg focusable="false" width="18" height="26" class="icon icon--form-error" viewBox="0 0 18 26"><circle cx="9" cy="13" r="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"></circle><path d="M8.993 15.262a.972.972 0 00-.979.968c0 .539.44.99.98.99a.99.99 0 00.978-.99.972.972 0 00-.979-.968zm-.78-.649h1.561V8.706H8.212v5.907z" fill="currentColor"></path></svg>';
      
      // Create content paragraph
      this.errorContent = document.createElement('p');
      this.errorContent.className = 'banner__content';
      
      this.errorBanner.appendChild(ribbon);
      this.errorBanner.appendChild(this.errorContent);
      
      // Insert at the beginning of the form
      this.form.insertBefore(this.errorBanner, this.form.firstChild);
    } else {
      // Use existing error banner content
      this.errorContent = this.errorBanner.querySelector('.banner__content');
    }
  }

  handleSubmit(event) {
    // Always prevent default form submission to avoid page refresh
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    const isValid = this.validateEmail();
    
    if (!isValid) {
      // Stop loader button animation - use setTimeout to ensure it runs after loader-button's handler
      setTimeout(() => {
        if (this.submitButton) {
          this.submitButton.removeAttribute('aria-busy');
          // Also remove the loader element if it was added
          const loader = this.submitButton.querySelector('.loader-button__loader');
          if (loader) {
            loader.remove();
          }
          // Restore original content if it was changed
          if (this.submitButton.originalContent) {
            this.submitButton.innerHTML = this.submitButton.originalContent;
          }
        }
      }, 0);
      
      // Focus on the error field
      this.emailInput.focus();
      
      return false;
    }
    
    // If valid, submit the form via AJAX or handle custom submission
    this.submitForm();
    return false;
  }

  submitForm() {
    // Submit form via AJAX to prevent page refresh
    const formData = new FormData(this.form);
    
    fetch(this.form.action, {
      method: this.form.method || 'POST',
      body: formData,
      headers: {
        'Accept': 'application/javascript'
      }
    })
    .then(response => response.text())
    .then(data => {
      // Handle success - replace input field with success banner
      console.log('Form submitted successfully');
      this.showSuccessBanner();
    })
    .catch(error => {
      console.error('Form submission error:', error);
      this.showError('An error occurred. Please try again.');
    })
    .finally(() => {
      // Stop loader button animation
      if (this.submitButton) {
        this.submitButton.removeAttribute('aria-busy');
        const loader = this.submitButton.querySelector('.loader-button__loader');
        if (loader) {
          loader.remove();
        }
      }
    });
  }

  showSuccessBanner() {
    // Create success banner
    const successBanner = document.createElement('div');
    successBanner.className = 'form__banner banner banner--success';
    successBanner.setAttribute('role', 'status');
    successBanner.setAttribute('aria-live', 'polite');
    successBanner.setAttribute('tabindex', '-1');
    
    // Create ribbon with icon
    const ribbon = document.createElement('span');
    ribbon.className = 'banner__ribbon';
    ribbon.innerHTML = '<svg focusable="false" width="26" height="26" class="icon icon--form-success" viewBox="0 0 26 26"><circle cx="13" cy="13" r="12" stroke="currentColor" stroke-width="1.5" fill="none"></circle><path d="M11.5 16.5l-3-3 1.5-1.5 1.5 1.5 4.5-4.5 1.5 1.5z" fill="currentColor"></path></svg>';
    
    // Create content paragraph
    const content = document.createElement('p');
    content.className = 'banner__content';
    
    // Get translation from Shopify theme translations
    const successMessage = window.themeVariables?.strings?.newsletterSuccess || 
                          'Thanks for subscribing!';
    content.textContent = successMessage;
    
    successBanner.appendChild(ribbon);
    successBanner.appendChild(content);
    
    // Find the input field container and replace it with success banner
    const inputWrapper = this.emailInput.closest('.form__input-wrapper') || this.emailInput.parentElement;
    if (inputWrapper) {
      inputWrapper.replaceWith(successBanner);
    }
    
    // Hide submit button
    if (this.submitButton) {
      this.submitButton.style.display = 'none';
    }
    
    // Clear any error messages
    this.clearError();
    
    // Focus on the success banner for accessibility
    successBanner.focus();
  }

  validateEmail() {
    const email = this.emailInput.value.trim();
    const fieldName = this.emailInput.labels[0]?.textContent.trim() || 'Email';
    
    // Check if empty
    if (!email) {
      this.showError(`${fieldName} is required`);
      return false;
    }
    
    // Check if valid email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      this.showError(`${fieldName} must be a valid email address (e.g., name@example.com)`);
      return false;
    }
    
    this.clearError();
    return true;
  }

  showError(message) {
    this.errorContent.textContent = message;
    this.errorBanner.style.display = 'flex';
    this.emailInput.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    this.errorContent.textContent = '';
    this.errorBanner.style.display = 'none';
    this.emailInput.setAttribute('aria-invalid', 'false');
  }
}

// Initialize validation for newsletter signup forms on page load
document.addEventListener('DOMContentLoaded', () => {
  // Only enhance actual newsletter forms (identified by a "newsletter" class).
  // Other forms that happen to contain an email input (e.g. register, login,
  // contact) must not be hijacked to show the newsletter success banner.
  const newsletterForms = document.querySelectorAll('form[class*="newsletter"]');

  newsletterForms.forEach(form => {
    if (form.querySelector('input[type="email"]')) {
      new NewsletterFormValidator(form);
    }
  });
});
